import { useCallback, useMemo, useState, useEffect } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { presentTodayKeys } from "@/hooks/usePresentToday";
import { useSearchParams } from "react-router";
import { Plus, RefreshCw } from "lucide-react";
import { Trans, useLingui } from "@lingui/react";
import type { PaginationState } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ActivitiesTable } from "@/components/table/ActivitiesTable";
import {
  AttendanceFilterBar,
  type AttendanceFilters,
} from "@/components/activities/AttendanceFilterBar";
import { PresentTodayBoard } from "@/components/activities/PresentTodayBoard";
import { useActivities } from "@/hooks/useActivities";
import { ActivityModal } from "@/components/activities";
import { parseDateString } from "@/utils/dateUtils";
import type { ActivityListParams } from "@/types/activities";

const DEFAULT_PAGE_SIZE = 20;
const ACTIVITIES_TAB = "activities";
const PRESENT_TODAY_TAB = "present-today";

/** Format a Date to a bare YYYY-MM-DD for the URL. */
function toDateParam(date?: Date): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function ActivitiesListPage() {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const attendanceFetching =
    useIsFetching({ queryKey: presentTodayKeys.all }) > 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const tab =
    searchParams.get("tab") === PRESENT_TODAY_TAB
      ? PRESENT_TODAY_TAB
      : ACTIVITIES_TAB;

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const pageSize = Math.min(
    100,
    Math.max(
      1,
      parseInt(
        searchParams.get("page_size") ?? String(DEFAULT_PAGE_SIZE),
        10,
      ) || DEFAULT_PAGE_SIZE,
    ),
  );

  // Derive the filter object (used by the filter bar) from URL state.
  const filters = useMemo<AttendanceFilters>(
    () => ({
      date_from: parseDateString(searchParams.get("date_from")),
      date_to: parseDateString(searchParams.get("date_to")),
      child_id: searchParams.get("child_id") ?? undefined,
      type: searchParams.get("type") ?? undefined,
      group_id: searchParams.get("group_id") ?? undefined,
      childcare_id: searchParams.get("childcare_id") ?? undefined,
    }),
    [searchParams],
  );

  // Build the query params. `date_to` is expanded to end-of-day so the whole
  // day's activities are included (backend filters activity_time <= date_to).
  const dateTo = useMemo(() => {
    if (!filters.date_to) return undefined;
    const d = new Date(filters.date_to);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [filters.date_to]);

  const queryParams = useMemo<ActivityListParams>(
    () => ({
      page,
      page_size: pageSize,
      child_id: filters.child_id,
      type: filters.type,
      date_from: filters.date_from,
      date_to: dateTo,
      group_id: filters.group_id,
      childcare_id: filters.childcare_id,
    }),
    [page, pageSize, filters, dateTo],
  );

  const { data, isLoading, isError, error, isFetching, refetch } =
    useActivities(queryParams);

  const refreshing =
    tab === PRESENT_TODAY_TAB ? attendanceFetching : isFetching;
  const refresh = () =>
    tab === PRESENT_TODAY_TAB
      ? queryClient.invalidateQueries({ queryKey: presentTodayKeys.all })
      : refetch();

  // Merge a set of param mutations into the URL, preserving the current tab.
  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setParam = (params: URLSearchParams, key: string, value?: string) => {
    if (value === undefined || value === "") {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  };

  // Filter change: write filter params + reset to page 1.
  const handleFilterChange = useCallback(
    (partial: Partial<AttendanceFilters>) => {
      updateParams((params) => {
        if ("date_from" in partial)
          setParam(params, "date_from", toDateParam(partial.date_from));
        if ("date_to" in partial)
          setParam(params, "date_to", toDateParam(partial.date_to));
        if ("child_id" in partial)
          setParam(params, "child_id", partial.child_id);
        if ("type" in partial) setParam(params, "type", partial.type);
        if ("group_id" in partial)
          setParam(params, "group_id", partial.group_id);
        if ("childcare_id" in partial)
          setParam(params, "childcare_id", partial.childcare_id);
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const handleClearFilters = useCallback(() => {
    updateParams((params) => {
      for (const key of [
        "date_from",
        "date_to",
        "child_id",
        "type",
        "group_id",
        "childcare_id",
      ]) {
        params.delete(key);
      }
      params.set("page", "1");
    });
  }, [updateParams]);

  // Server-side pagination state (0-based pageIndex for the table).
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: page - 1, pageSize }),
    [page, pageSize],
  );

  const handlePaginationChange = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState),
    ) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      updateParams((params) => {
        params.set("page", String(next.pageIndex + 1));
        params.set("page_size", String(next.pageSize));
      });
    },
    [pagination, updateParams],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      updateParams((params) => {
        params.set("tab", value);
      });
    },
    [updateParams],
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState<
    string | undefined
  >(undefined);
  const [modalMode, setModalMode] = useState<"view" | "create" | "edit">(
    "view",
  );

  const handleCreateActivity = useCallback(() => {
    setSelectedActivityId(undefined);
    setModalMode("create");
    setModalOpen(true);
  }, []);

  // Open create modal when navigated with ?action=create. Consume the flag by
  // removing it from the URL so later filter/pagination changes don't reopen it.
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      handleCreateActivity();
      updateParams((params) => {
        params.delete("action");
      });
    }
  }, [searchParams, handleCreateActivity, updateParams]);

  const openActivity = useCallback((activityId: string) => {
    setSelectedActivityId(activityId);
    setModalMode("view");
    setModalOpen(true);
  }, []);

  // Open one record when navigated with ?activity=<id> (the accident link in
  // the workflow inbox). Consumed like ?action=create, so later filter or page
  // changes don't reopen it.
  useEffect(() => {
    if (!searchParams.has("activity")) return;
    const linkedId = searchParams.get("activity");
    if (linkedId) openActivity(linkedId);
    updateParams((params) => {
      params.delete("activity");
    });
  }, [searchParams, openActivity, updateParams]);

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        className="flex-none"
        title={<Trans id="nav.attendanceActivities">Attendance & Activities</Trans>}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={refresh}
              disabled={refreshing}
              title={i18n._({ id: "common.refresh", message: "Refresh" })}
              aria-label={i18n._({ id: "common.refresh", message: "Refresh" })}
            >
              <RefreshCw
                className={cn("h-4 w-4", refreshing && "animate-spin")}
                aria-hidden="true"
              />
            </Button>
            <Button
              onClick={handleCreateActivity}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>
                <Trans id="common.add">Add</Trans>
              </span>
            </Button>
          </>
        }
      />

      <Tabs
        value={tab}
        onValueChange={handleTabChange}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <TabsList className="flex-none self-start">
          <TabsTrigger value={ACTIVITIES_TAB}>
            {i18n._({
              id: "activities.tabs.activities",
              message: "Activities",
            })}
          </TabsTrigger>
          <TabsTrigger value={PRESENT_TODAY_TAB}>
            {i18n._({
              id: "activities.tabs.presentToday",
              message: "Present Today",
            })}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value={ACTIVITIES_TAB}
          className="min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=active]:flex data-[state=inactive]:hidden"
        >
          <div className="flex-none">
            <AttendanceFilterBar
              filters={filters}
              onChange={handleFilterChange}
              onClear={handleClearFilters}
            />
          </div>

          <div className="flex-1 overflow-auto">
            {isError ? (
              <QueryErrorState
                error={error}
                onRetry={() => void refetch()}
                title={
                  <Trans id="activities.error.loadFailed">
                    Failed to load activities
                  </Trans>
                }
              />
            ) : (
              // While the first page loads the table shows skeleton rows, so
              // the toolbar and filters above stay where they are.
              <ActivitiesTable
                isLoading={isLoading}
                data={data?.items ?? []}
                enableSelection={true}
                onRowClick={openActivity}
                serverSidePagination={true}
                totalRows={data?.total ?? 0}
                pagination={pagination}
                onPaginationChange={handlePaginationChange}
                isFetching={isFetching}
                exportParams={queryParams}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent
          value={PRESENT_TODAY_TAB}
          className="min-h-0 flex-1 overflow-auto data-[state=inactive]:hidden"
        >
          <PresentTodayBoard />
        </TabsContent>
      </Tabs>

      {/* Activity Modal */}
      <ActivityModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        activityId={selectedActivityId}
        initialMode={modalMode}
      />
    </PageContainer>
  );
}
